import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { NgChartsModule } from 'ng2-charts';
import { ChartOptions, ChartType, ChartData } from 'chart.js';
import {
  BarChartWidgetData,
  ChartCategorieMonthFilter,
  ChartMonthFilter,
  ChartYearFilter,
} from 'src/app/core/providers/dashboardWidget/dashboardWidget.models';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import {
  CategoriesListResponse,
  Category,
} from 'src/app/core/providers/category/category.models';
import { SharedModule } from 'src/app/shared/shared.module';
import { Subscription } from 'rxjs';
import { ApiService } from 'src/app/core/providers/api/api.service';

@Component({
  selector: 'app-bar-chart',
  standalone: true,
  imports: [
    NgChartsModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    NgbDropdownModule,
    SharedModule,
  ],
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss',
})
export class BarChartComponent implements OnInit, OnChanges {
  @Input() widgetData!: BarChartWidgetData;
  title?: string = '';
  availableMonths: number[] = [];
  selectedMonths?: number[] = [];
  activateCategory?: boolean = false;
  selectedCategoryCodes: string[] = [];
  availableCategories: Category[] = [];
  barChartType: ChartType = 'bar';
  barChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (event, activeElements) => {
      if (event.native && event.native.target) {
        const target = event.native.target as HTMLElement;
        target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
        },
        stacked: false,
      },
      y: {
        grid: {
          display: false,
        },
        beginAtZero: true,
        stacked: false,
        ticks: {
          stepSize: 1,
          callback: function(value: any) {
            return Number.isInteger(value) ? value : '';
          }
        }
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    datasets: {
      bar: {
        barThickness: 5, // Largeur fixe de chaque barre
        maxBarThickness: 5, // Limite la largeur max
        categoryPercentage: 0.5, // Optionnel pour gérer l'espace entre les groupes
        barPercentage: 0.6, // Optionnel pour gérer l'espace entre les barres d'un même groupe
      },
    },
  };
  barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [],
  };
  subscription: Subscription = new Subscription();

  constructor(
    public translate: TranslateService,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    this.initWidgetData();
    this.fetchAvailableCategories();
  }

  fetchAvailableCategories() {
    this.subscription.add(
      this.apiService
        .getAll('categories', 1, 100, { 'order[filter]': 'ASC' })
        .subscribe({
        next: (response: CategoriesListResponse) => {
          this.availableCategories = [...response.member];
        },
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widgetData'] && !changes['widgetData'].firstChange) {
      this.widgetData = { ...this.widgetData };
      this.initWidgetData();
    }
  }

  initWidgetData(): void {
    if (this.widgetData) {
      this.title = this.widgetData.title;
      this.barChartData = {
        labels: [...this.widgetData.labels],
        datasets: this.widgetData.datasets.map((ds) => ({ ...ds })),
      };

      // Gestion du filtre combiné
      const filterConfig = this.widgetData.filterConfig as
        | ChartCategorieMonthFilter
        | ChartYearFilter
        | ChartMonthFilter;

      if (filterConfig) {
        // Catégories
        if (
          'activateCategory' in filterConfig &&
          filterConfig.activateCategory
        ) {
          this.activateCategory = filterConfig.activateCategory;
        }
        if (
          'selectedCategoryCodes' in filterConfig &&
          Array.isArray(filterConfig.selectedCategoryCodes)
        ) {
          this.selectedCategoryCodes = filterConfig.selectedCategoryCodes;
        }
        // Mois
        if ('availableMonths' in filterConfig && filterConfig.availableMonths) {
          this.availableMonths = filterConfig.availableMonths;
          const sel = filterConfig.selectedMonths;
          this.selectedMonths = Array.isArray(sel) ? sel : sel ? [sel] : [];
        }
      }
    }
  }

  toggleMonth(month: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.selectedMonths) {
      this.selectedMonths = [];
    }
    const idx = this.selectedMonths.indexOf(month);
    if (idx === -1) {
      this.selectedMonths = [...this.selectedMonths, month];
    } else {
      this.selectedMonths = this.selectedMonths.filter((m) => m !== month);
    }
    this.onMonthsSelect(this.selectedMonths ?? []);
  }

  areAllMonthsSelected(): boolean {
    if (!this.availableMonths.length || !this.selectedMonths) {
      return false;
    }
    return this.availableMonths.length === this.selectedMonths.length &&
           this.availableMonths.every(month => this.selectedMonths?.includes(month));
  }

  toggleAllMonths(event: Event) {
    event.stopPropagation();
    if (this.areAllMonthsSelected()) {
      // Décocher tous les mois
      this.selectedMonths = [];
    } else {
      // Cocher tous les mois
      if (this.availableMonths.length > 0) {
        this.selectedMonths = [...this.availableMonths];
      }
    }
    this.onMonthsSelect(this.selectedMonths ?? []);
  }

  onMonthsSelect(months: number[]) {
    if (
      this.widgetData.filterConfig &&
      'onMonthsChanged' in this.widgetData.filterConfig &&
      this.widgetData?.filterConfig?.onMonthsChanged
    ) {
      this.widgetData.filterConfig.onMonthsChanged(months);
    }
  }

  // Méthode pour gérer la sélection multiple
  onCategoryToggle(category: Category) {
    const idx = this.selectedCategoryCodes.indexOf(category.code);
    if (idx === -1) {
      this.selectedCategoryCodes = [
        ...this.selectedCategoryCodes,
        category.code,
      ];
    } else {
      this.selectedCategoryCodes = this.selectedCategoryCodes.filter(
        (code) => code !== category.code
      );
    }
    if (
      this.widgetData.filterConfig &&
      'onCategoryChanged' in this.widgetData.filterConfig &&
      this.widgetData.filterConfig.onCategoryChanged
    ) {
      this.widgetData.filterConfig.onCategoryChanged(
        this.selectedCategoryCodes
      );
    }
  }

  onBarClick(event: any) {
    if (event.active && event.active.length > 0) {
      const chartElement = event.active[0];
      const datasetIndex = chartElement.datasetIndex;
      const dataIndex = chartElement.index;

      const status = this.barChartData.datasets[datasetIndex].label;
      const extraInfo = this.widgetData.extraInfo ?? [];

      let infos: any[] = [];
      if (
        typeof status === 'string' &&
        extraInfo[dataIndex] &&
        typeof extraInfo[dataIndex][status] !== 'undefined'
      ) {
        infos = extraInfo[dataIndex][status];
      }
      this.widgetData.onBarClick?.({
        extraInfo: infos,
        filter: this.widgetData.filterConfig?.filtre,
      });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
